import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'common/hooks/useTranslation';
import { toast } from 'react-toastify';
import { Button, CircularProgress } from '@mui/material';
import { useActiveWorkSession } from 'app/installation/hooks/useActiveWorkSession';
import { useWorkSessionActions } from 'app/installation/hooks/useWorkSessionActions';
import { useCanModifyInstallation } from 'app/installation/hooks/useCanModifyInstallation';

import { styles } from './InstallationButton.styles';

export type InstallationButtonProps = {
  propertyId: number | null;
  onStartClick?: () => void;
  onEndClick?: () => void;
  disabled?: boolean;
};
export const InstallationButton: FC<InstallationButtonProps> = ({
  propertyId,
  onStartClick,
  onEndClick,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const { id: installationId, createdBy, isInstallationOngoing, isLoading } = useActiveWorkSession(propertyId);
  const { canModify } = useCanModifyInstallation(createdBy);

  const { startWorkSession, endWorkSession, isStarting, isEnding } = useWorkSessionActions(propertyId, {
    onStarted: () => toast.success(t('installation.started')),
    onStartError: () => {
      toast.error(t('installation.error.start'));
    },
    onEnded: () => toast.success(t('installation.done')),
    onEndError: () => {
      toast.error(t('installation.error.end'));
    },
  });

  const isBusy = useMemo(() => isLoading || isStarting || isEnding, [isLoading, isStarting, isEnding]);

  const isDisabled = useMemo(() => {
    return disabled || isBusy || !canModify;
  }, [disabled, isBusy, canModify]);

  const handleOnClick = useCallback(async () => {
    try {
      if (isInstallationOngoing) {
        if (installationId == null) return;
        await endWorkSession(installationId);
        onEndClick?.();
        return;
      }

      await startWorkSession();

      onStartClick?.();
    } catch {
      // Error handled by onStartError/onEndError callbacks
    }
  }, [isInstallationOngoing, installationId, startWorkSession, endWorkSession, onStartClick, onEndClick]);

  const textKey = useMemo(
    () => (isInstallationOngoing ? 'installation.end' : 'installation.start'),
    [isInstallationOngoing],
  );

  return (
    <Button variant="contained" disabled={isDisabled} sx={!isDisabled ? styles.button : {}} onClick={handleOnClick}>
      {isBusy ? <CircularProgress size={16} /> : t(textKey)}
    </Button>
  );
};
